import React, { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_API_URL}`;

const AVATAR_COLORS = [
  'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
  'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
  'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
  'linear-gradient(135deg, #10b981 0%, #059669 100%)',
];

function getAvatarStyle(str) {
  let hash = 0;
  for (let c of (str || '')) hash += c.charCodeAt(0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function getAvatarLabel(str) {
  if (!str) return '50';
  const nums = str.match(/\d+/g);
  if (nums) return nums.join('').slice(0, 2);
  return str.slice(0, 2).toUpperCase();
}

function UploadForm({ patient, user, onBack, onDone }) {
  const [file, setFile] = useState(null);
  const [reportType, setReportType] = useState('Blood Analysis');
  const [hospitalName, setHospitalName] = useState(user?.name || '');
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(false);
  const [patientUploads, setPatientUploads] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/Provider/my-uploads/${user.id}`)
      .then(r => r.json())
      .then(data => setPatientUploads(data.filter(u => u.patientUid === patient.patientUid)))
      .catch(() => {});
  }, [patient.patientUid, user.id]);

  const handleUpload = async () => {
    if (!file) { setMessage('❌ Please select a file.'); return; }
    if (!reportType) { setMessage('❌ Please select report type.'); return; }
    if (!hospitalName.trim()) { setMessage('❌ Please enter facility name.'); return; }
    setLoading(true); setMessage('');
    try {
      const formData = new FormData();
      formData.append('ProviderId', user.id);
      formData.append('PatientUid', patient.patientUid);
      formData.append('ReportType', reportType);
      formData.append('HospitalName', hospitalName);
      formData.append('File', file);
      const res = await fetch(`${API}/api/Provider/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (res.ok) { setDone(true); setMessage(data.message); }
      else setMessage(`❌ ${data.message}`);
    } catch { setMessage('❌ Cannot connect to server.'); }
    setLoading(false);
  };

  if (done) return (
    <div className="px-4 py-10 flex flex-col items-center text-center space-y-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="w-20 h-20 rounded-full flex items-center justify-center bg-emerald-100/70">
        <span className="material-symbols-outlined text-emerald-600" style={{ fontSize: '48px', fontVariationSettings: '"FILL" 1' }}>check_circle</span>
      </div>
      <h2 className="text-xl font-extrabold text-gray-900">Report Uploaded!</h2>
      <p className="text-gray-500 text-sm font-medium">{message}</p>
      <p className="text-xs text-gray-400">Patient will receive a notification to accept.</p>
      <button 
        onClick={onDone} 
        className="w-full py-3.5 rounded-2xl text-white font-bold active:scale-95 transition-all shadow-md mt-2"
        style={{ background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)' }}
      >
        ← Back to Patients
      </button>
    </div>
  );

  return (
    <div className="p-4 space-y-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      {/* Patient Banner */}
      <section className="bg-white rounded-3xl p-5 shadow-sm border border-purple-100 flex items-center gap-4">
        <div 
          className="w-14 h-14 rounded-full flex items-center justify-center text-white font-extrabold text-lg shrink-0 shadow-sm"
          style={{ background: getAvatarStyle(patient.patientUid) }}
        >
          {getAvatarLabel(patient.patientUid)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-base text-gray-900 truncate">{patient.patientName || patient.patientUid}</p>
          <p className="text-xs font-mono font-bold text-gray-400 mt-0.5">{patient.patientUid}</p>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 mt-1">
            <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span> Approved
          </span>
        </div>
      </section>

      {/* Form Details */}
      <section className="bg-white rounded-3xl p-5 shadow-sm border border-slate-100 space-y-4">
        <h2 className="font-extrabold text-base text-gray-900">Upload New Report</h2>

        <div>
          <p className="text-xs font-bold text-gray-500 mb-1.5">Report Type</p>
          <div className="flex items-center border border-slate-200 rounded-2xl px-3.5 py-3 focus-within:border-purple-500">
            <select 
              value={reportType} 
              onChange={e => setReportType(e.target.value)}
              className="bg-transparent outline-none text-sm font-medium w-full text-gray-900"
            >
              {['Blood Analysis','Blood Test','Urine Test','MRI Scan','X-Ray','CT Scan','ECG','Ultrasound','Biopsy','Covid Test','Other'].map(t => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <p className="text-xs font-bold text-gray-500 mb-1.5">Facility Name</p>
          <div className="flex items-center border border-slate-200 rounded-2xl px-3.5 py-3 focus-within:border-purple-500">
            <input 
              type="text" 
              value={hospitalName} 
              onChange={e => setHospitalName(e.target.value)}
              placeholder="Apollo Diagnostics"
              className="bg-transparent outline-none text-sm font-medium w-full text-gray-900 placeholder-gray-400" 
            />
          </div>
        </div>

        <div>
          <p className="text-xs font-bold text-gray-500 mb-1.5">Visit Date</p>
          <div className="flex items-center gap-2 border border-slate-200 rounded-2xl px-3.5 py-3 focus-within:border-purple-500">
            <span className="material-symbols-outlined text-gray-400 text-lg">calendar_month</span>
            <input 
              type="date" 
              value={visitDate} 
              onChange={e => setVisitDate(e.target.value)}
              className="bg-transparent outline-none text-sm font-medium w-full text-gray-900" 
            />
          </div>
        </div>

        <div>
          <p className="text-xs font-bold text-gray-500 mb-2">Attach File</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Camera', icon: 'photo_camera', accept: 'image/*', capture: 'environment' },
              { label: 'Gallery', icon: 'image', accept: 'image/*' },
              { label: 'PDF', icon: 'description', accept: '.pdf,.jpg,.jpeg,.png' },
              { label: 'Scan', icon: 'document_scanner', accept: 'image/*', capture: 'environment' },
            ].map(opt => (
              <label 
                key={opt.label}
                className="flex flex-col items-center justify-center gap-1 py-3 rounded-2xl border-2 border-dashed border-slate-200 cursor-pointer hover:border-purple-500 active:scale-95 transition-all"
              >
                <span className="material-symbols-outlined text-gray-400 text-xl">{opt.icon}</span>
                <span className="text-[10px] text-gray-600 font-bold text-center leading-tight">{opt.label}</span>
                <input 
                  type="file" 
                  accept={opt.accept} 
                  capture={opt.capture}
                  onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} 
                  className="hidden" 
                />
              </label>
            ))}
          </div>
          {file && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-2xl px-3.5 py-2.5 mt-2">
              <span className="material-symbols-outlined text-emerald-600 text-base" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>
              <p className="text-xs font-bold text-emerald-800 truncate flex-1">{file.name}</p>
              <button onClick={() => setFile(null)}>
                <span className="material-symbols-outlined text-gray-400 text-base">close</span>
              </button>
            </div>
          )}
        </div>

        {message && !done && (
          <div className={`p-3 rounded-2xl text-xs font-bold ${message.includes('❌') ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`}>
            {message}
          </div>
        )}

        <button 
          onClick={handleUpload} 
          disabled={loading}
          className="w-full py-4 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 transition-all shadow-md mt-2"
          style={{ background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)' }}
        >
          <span className="material-symbols-outlined text-lg">cloud_upload</span>
          {loading ? 'Uploading...' : 'Upload Report'}
        </button>
      </section>

      {/* History */}
      {patientUploads.length > 0 && (
        <section className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-5 pt-4 pb-3">
            <h3 className="font-extrabold text-sm text-gray-900">Previously Uploaded</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {patientUploads.map((u, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3">
                <div 
                  className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: u.status === 'accepted' ? '#e3f7ea' : u.status === 'pending' ? '#fdf1de' : '#fde2e2' }}
                >
                  <span className="material-symbols-outlined text-xl" style={{ color: u.status === 'accepted' ? '#16a34a' : u.status === 'pending' ? '#c9791a' : '#dc2626' }}>
                    description
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-gray-900 truncate">{u.fileName}</p>
                  <p className="text-xs text-gray-400 font-medium">{u.reportType} • {new Date(u.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                </div>
                <span 
                  className="text-[10px] font-extrabold px-2 py-0.5 rounded-full shrink-0"
                  style={{
                    background: u.status === 'accepted' ? '#e3f7ea' : u.status === 'pending' ? '#fdf1de' : '#fde2e2',
                    color: u.status === 'accepted' ? '#16a34a' : u.status === 'pending' ? '#c9791a' : '#dc2626'
                  }}
                >
                  {u.status.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function MobileProviderUpload({ user }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ProviderAccess/approved-patients/${user.id}`);
      const data = await res.json();
      setPatients(data);
    } catch { }
    setLoading(false);
  }, [user.id]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  if (selectedPatient) return (
    <div className="min-h-screen bg-slate-50/50 pb-6" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div className="bg-white px-4 py-3 flex items-center gap-3 border-b border-slate-100">
        <button 
          onClick={() => setSelectedPatient(null)} 
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-gray-600"
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <p className="font-extrabold text-base text-gray-900">Upload Report</p>
      </div>
      <UploadForm 
        patient={selectedPatient} 
        user={user}
        onBack={() => setSelectedPatient(null)}
        onDone={() => { setSelectedPatient(null); fetchPatients(); }} 
      />
    </div>
  );

  return (
    <div className="p-4 space-y-4" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      
      {/* Title & Info Button */}
      <div className="flex items-start justify-between pt-1">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Upload Reports</h1>
          <p className="text-xs font-semibold text-gray-400 mt-1">Select an approved patient to upload reports</p>
        </div>
        <button className="w-9 h-9 rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center text-purple-600">
          <span className="material-symbols-outlined text-xl">info</span>
        </button>
      </div>

      {loading && <p className="text-center text-gray-400 text-sm py-8 font-medium">Loading patients...</p>}

      {!loading && patients.length === 0 && (
        <div className="bg-white rounded-3xl p-8 text-center border border-slate-100 shadow-sm space-y-2">
          <div className="w-16 h-16 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center mx-auto">
            <span className="material-symbols-outlined text-3xl">group</span>
          </div>
          <p className="font-extrabold text-gray-900 text-base">No approved patients yet</p>
          <p className="text-xs text-gray-400 font-medium">Search a patient and send an access request first</p>
        </div>
      )}

      {/* Patient List */}
      <div className="space-y-3">
        {patients.map((p, i) => (
          <button 
            key={i} 
            onClick={() => setSelectedPatient(p)}
            className="w-full bg-white rounded-3xl p-4 flex items-center justify-between shadow-sm border border-slate-100/80 active:scale-[0.99] transition-all text-left"
          >
            <div className="flex items-center gap-3.5 min-w-0">
              <div 
                className="w-14 h-14 rounded-full flex items-center justify-center text-white font-black text-lg shrink-0 shadow-sm"
                style={{ background: getAvatarStyle(p.patientUid) }}
              >
                {getAvatarLabel(p.patientUid)}
              </div>
              <div className="min-w-0">
                <p className="font-extrabold text-base text-gray-900 truncate leading-snug">{p.patientName || 'Patient'}</p>
                <p className="text-xs font-mono font-medium text-gray-400 mt-0.5 tracking-tight">{p.patientUid}</p>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold mt-1.5 bg-emerald-50 text-emerald-600 border border-emerald-100">
                  <span className="material-symbols-outlined text-[12px]" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span> Approved
                </span>
              </div>
            </div>

            <div className="w-10 h-10 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center shrink-0 ml-2">
              <span className="material-symbols-outlined text-xl">chevron_right</span>
            </div>
          </button>
        ))}
      </div>

      {/* Privacy Notice Card */}
      <div className="bg-gradient-to-r from-purple-50/60 to-blue-50/60 border border-purple-100/80 rounded-3xl p-4 flex items-center gap-3.5 mt-4">
        <div className="w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-purple-600 shrink-0 shadow-sm">
          <span className="material-symbols-outlined text-xl" style={{ fontVariationSettings: '"FILL" 1' }}>verified_user</span>
        </div>
        <div>
          <p className="font-extrabold text-xs text-gray-900">Upload only relevant medical reports</p>
          <p className="text-[11px] font-medium text-gray-500 leading-tight mt-0.5">Ensure patient data privacy and upload accurate documents.</p>
        </div>
      </div>

    </div>
  );
}

export default MobileProviderUpload;